import memoize from "memoizee";
import groupBy from "lodash/groupBy";
import type { UseQueryResult } from "react-query";
import type { AxiosError } from "axios";

import {
  FieldsResponse,
  BasicField,
  BasicFieldsDataEnvelope,
} from "../../../models/fields";
import { convertDataToDataById } from "../../../helpers/computations";
import { getAuthToken } from "../../../services/granularAxios";
import { ensureValue } from "../../../services/util";
import {
  FetcherQueryKey,
  QueryFunction,
  queryClient,
} from "../../../services/query";
import fieldsService from "../../../services/fields";
import {
  numberOfFieldsListsPossiblyActiveAtTheSameTime,
  refinedListOfFields,
} from "../../../services/helpers/refinements";
import { getActiveFields } from "../../../hooks/fields.helpers";

export type FieldsFetcherParams = {
  userId: string | undefined;
  operationId: string | undefined;
  catalogFlag: boolean | undefined;
};
export type FieldsQueryKey = FetcherQueryKey<FieldsFetcherParams>;

export const fieldsQueryKeyPrefix = "[@fabric/core][fields]";

/**
 * Computes the fieldsQueryKey from the given params.
 */
export const computeFieldsQueryKey = (
  params: FieldsFetcherParams,
): FieldsQueryKey => [fieldsQueryKeyPrefix, params];

/**
 * Fetches the fields using the fieldsService.
 */
export const fieldsFetcher: QueryFunction<FieldsResponse, FieldsFetcherParams> =
  async (context) => {
    const userId = ensureValue(context.queryKey[1].userId, "userId");
    const operationId = ensureValue(
      context.queryKey[1].operationId,
      "operationId",
    );
    const catalogFlag = ensureValue(
      context.queryKey[1].catalogFlag,
      "catalogFlag",
    );

    // IMPORTANT: We are not passing the authToken as part of the queryKey
    // because we don't want to invalidate our data on auth token change.
    // The auth token can and will change throughout the lifetime of the app,
    // but that should not invalidate our data.
    const authToken = getAuthToken();

    const existingCacheEntry = queryClient
      .getQueryCache()
      .find<FieldsResponse>(context.queryKey);

    // Get the fields sync data, which will include the field_id and last_modified of each field.
    const fieldsSyncDataResponse = await fieldsService.getFieldsSyncData(
      {
        authToken,
        userId,
        operationId,
      },
      catalogFlag,
    );

    // Calculate a hash of the attributes of the fields based on when they were last modified.
    const attributesHash =
      // Prepend the hash with a version identifier for the hash calculation.
      // Change this version if we change how we calculate the hash.
      ["v1"]
        .concat(
          // Calculate the hash
          fieldsSyncDataResponse.attributes.active.map(
            (x) => `${x.field_id}-${x.last_modified}`,
          ),
        )
        .join(" ");

    // Calculate a hash of the revisions of the fields based on when they were last modified.
    const revisionsHash =
      // Prepend the hash with a version identifier for the hash calculation.
      // Change this version if we change how we calculate the hash.
      ["v1"]
        .concat(
          // Calculate the hash
          fieldsSyncDataResponse.revisions.active.map(
            (x) => `${x.field_revision_id}-${x.last_modified}`,
          ),
        )
        .join(" ");

    const existingData = existingCacheEntry?.state.data;

    if (
      existingData &&
      existingData.attributesHash === attributesHash &&
      existingData.revisionsHash === revisionsHash
    ) {
      // If the hashes of the existing data and the new data match,
      // we will assume the data has not changed and thus it is not worth fetching.
      // Instead, simply return the existing data.
      return existingData;
    }

    // Otherwise, we must fetch the new data and return that, coupled with the calculated hashes.
    const fieldsResponse = await fieldsService.getFields(
      {
        authToken,
        userId,
        operationId,
      },
      catalogFlag,
    );
    return { ...fieldsResponse, attributesHash, revisionsHash };
  };

/**
 * Compute the activeFields derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeActiveFields = memoize(
  (fields: BasicField[]) => {
    return getActiveFields({ fields });
  },
  {
    max: numberOfFieldsListsPossiblyActiveAtTheSameTime,
    profileName: "[fields.query.helpers] computeActiveFields",
  },
);

/**
 * Compute the fieldsByFarmId derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeFieldsByFarmId = memoize(
  (fields: BasicField[]) => {
    const grouped = groupBy(fields, "farmId");
    delete grouped["null"];
    return grouped;
  },
  {
    max: numberOfFieldsListsPossiblyActiveAtTheSameTime,
    profileName: "[fields.query.helpers] computeFieldsByFarmId",
  },
);

/**
 * Compute the fieldsById derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeFieldsById = memoize(convertDataToDataById, {
  max: numberOfFieldsListsPossiblyActiveAtTheSameTime,
  profileName: "[fields.query.helpers] computeFieldsById",
});

/**
 * Compute the basicFields data envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelope = memoize(
  (
    fields: BasicField[],
    fieldsById: Record<string, BasicField | undefined>,
    fieldsByFarmId: Record<string, BasicField[] | undefined>,
    activeFields: BasicField[],
  ): BasicFieldsDataEnvelope => {
    return { fields, fieldsById, fieldsByFarmId, activeFields };
  },
  {
    max: numberOfFieldsListsPossiblyActiveAtTheSameTime,
    profileName: "[fields.query.helpers] computeDataEnvelope",
  },
);

/**
 * Compute the data envelope from the response.
 *
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * There is no need for this function itself to be memoized
 * because its called functions are memoized and its result is memoized.
 */
export const computeDataEnvelopeFromResponse = (
  fieldsResponse: FieldsResponse,
): BasicFieldsDataEnvelope => {
  // Compute the derived data for the data envelope.
  // (Don't worry. These compute functions are globally memoized.)
  const fields = refinedListOfFields(fieldsResponse.fields);
  const fieldsById = computeFieldsById(fields);
  const fieldsByFarmId = computeFieldsByFarmId(fields);
  const activeFields = computeActiveFields(fields);
  const dataEnvelope = computeDataEnvelope(
    fields,
    fieldsById,
    fieldsByFarmId,
    activeFields,
  );
  return dataEnvelope;
};

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in useFieldsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<FieldsResponse, AxiosError>,
): UseQueryResult<BasicFieldsDataEnvelope, AxiosError> => {
  const dataEnvelope = resultOfUseQuery.data
    ? computeDataEnvelopeFromResponse(resultOfUseQuery.data)
    : undefined;

  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<BasicFieldsDataEnvelope, AxiosError>;
};

/**
 * Returns whether the current list of fields is equal to the previous.
 *
 * This is determined by comparing the revision ids of the fields.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useFieldsQuery.
 */
export const isDataEqual = (
  prevResponse: FieldsResponse | undefined,
  response: FieldsResponse,
): boolean => {
  if (!prevResponse) {
    return false;
  }

  if (prevResponse.count !== response.count) {
    return false;
  }

  return (
    prevResponse.attributesHash === response.attributesHash &&
    prevResponse.revisionsHash === response.revisionsHash
  );
};
